import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Search, Stethoscope, ShieldCheck, BadgeCheck, Award,
         UserCircle2, MessageCircle, HeartHandshake } from "lucide-react";
import { trackWhatsApp } from "../lib/beacon";

const SUPPORT_WA = process.env.REACT_APP_SUPPORT_WA || "+5511996420785";
const digits = (s) => (s || "").replace(/[^0-9]/g, "");

const TIER_STYLES = {
  ouro:   { label: "Ouro",   ring: "ring-[#D4A017]", bg: "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]" },
  prata:  { label: "Prata",  ring: "ring-slate-400", bg: "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900" },
  bronze: { label: "Bronze", ring: "ring-amber-700", bg: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white" },
};

export default function FPProfessionals() {
  const [pros, setPros] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "", city: "", modality: "", source: "todos" });
  const [onCall, setOnCall] = useState(null);

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.category) params.category = f.category;
      if (f.city) params.city = f.city;
      if (f.modality) params.modality = f.modality;
      if (f.source && f.source !== "todos") params.source = f.source;
      const { data } = await api.get("/fp/professionals", { params });
      setPros(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/fp/professionals/categories").then((r) => setCats(r.data.professional_categories || []));
    api.get("/public/on-call").then((r) => setOnCall(r.data)).catch(() => {});
    load({ source: "todos" });
    // eslint-disable-next-line
  }, []);

  // Recarrega quando o source muda (client toggle)
  useEffect(() => {
    load(filters);
    // eslint-disable-next-line
  }, [filters.source]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="fp-pros-list">
      <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-bold inline-flex items-center gap-1.5">
        <HeartHandshake className="w-3.5 h-3.5" /> Profissionais Filho Presente
      </div>
      <h1 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl tracking-tight mt-2">
        Cuidado geriátrico com <span className="text-emerald-700">profissionais verificados</span>
      </h1>
      <p className="text-slate-600 mt-3 leading-relaxed text-sm max-w-3xl">
        Especialistas em cuidado do idoso e coordenação familiar — todos com <b>credencial validada</b> e <b>selo RAF</b>.
        Para agendar, fale com o consultor de plantão: ele conhece cada profissional e indica quem melhor se encaixa no seu caso.
      </p>

      {/* Toggle de origem — igual ao RAF pra manter a experiência consistente. */}
      <div className="mt-6 inline-flex items-center gap-1 border border-slate-200 rounded-full p-1 bg-white">
        {[
          { v: "todos", l: "Todos" },
          { v: "fp",    l: "Só Filho Presente" },
          { v: "raf",   l: "Só RAF" },
        ].map((t) => (
          <button key={t.v} onClick={() => setFilters({ ...filters, source: t.v })}
            data-testid={`fp-source-${t.v}`}
            className={`text-xs px-4 py-1.5 rounded-full transition-colors ${
              filters.source === t.v
                ? (t.v === "fp" ? "bg-amber-500 text-white" : "bg-[#0B2B62] text-white")
                : "text-slate-600 hover:bg-slate-50"
            }`}>{t.l}</button>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mt-4 mb-6 grid sm:grid-cols-4 gap-3">
        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          data-testid="fp-filter-category"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Todas categorias</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input placeholder="Cidade" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}
          data-testid="fp-filter-city"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
        <select value={filters.modality} onChange={(e) => setFilters({ ...filters, modality: e.target.value })}
          data-testid="fp-filter-modality"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Todas modalidades</option>
          <option value="presencial">Presencial</option>
          <option value="online">Online</option>
          <option value="domiciliar">Domiciliar</option>
        </select>
        <button onClick={() => load()} data-testid="fp-filter-apply"
          className="bg-[#0B2B62] text-white px-4 py-2 rounded-md hover:bg-slate-800 text-sm inline-flex items-center justify-center gap-2">
          <Search className="w-4 h-4" /> Buscar
        </button>
      </div>

      {loading ? <div className="text-slate-500">Carregando…</div>
        : pros.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-10 text-center">
            <Stethoscope className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <div className="text-slate-700">
              {filters.source === "fp"
                ? "Nenhum especialista Filho Presente com esses filtros. Alterne pra “Todos”."
                : filters.source === "raf"
                ? "Nenhum profissional RAF-original com esses filtros. Alterne pra “Todos”."
                : "Nenhum profissional encontrado com os filtros atuais."}
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="fp-pros-grid">
            {pros.map((p) => <FPProfCard key={p.id} p={p} onCall={onCall} />)}
          </div>
        )}
    </div>
  );
}

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map(s => s[0] || "").join("").toUpperCase() || "?";

const AVATAR_TIER_BG = {
  ouro: "bg-gradient-to-br from-amber-300 to-amber-600 text-amber-950",
  prata: "bg-gradient-to-br from-slate-300 to-slate-500 text-slate-900",
  bronze: "bg-gradient-to-br from-orange-500 to-orange-800 text-white",
};

const FPProfCard = ({ p, onCall }) => {
  const tier = (p.certification_tier || "").toLowerCase();
  const style = TIER_STYLES[tier];
  const target = digits(onCall?.online ? (onCall.phone || SUPPORT_WA) : SUPPORT_WA);
  const consultorName = onCall?.online && onCall?.first_name ? onCall.first_name : "consultor RAF";
  const msg = `Olá, ${consultorName}. Vi o perfil de ${p.display_name || "profissional"} no Filho Presente e gostaria de mais informações e agendamento.`;
  const waUrl = `https://wa.me/${target}?text=${encodeURIComponent(msg)}`;
  const cats = p.categories && p.categories.length ? p.categories : (p.category ? [p.category] : []);
  const council = [p.council_type, p.council_state].filter(Boolean).join(" · ");
  const avatarBg = AVATAR_TIER_BG[tier] || "bg-gradient-to-br from-emerald-400 to-emerald-700 text-white";

  return (
    <div data-testid={`fp-pro-card-${p.id}`}
      className="bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-500 hover:shadow-md transition-all flex flex-col relative min-h-[280px]">
      <span className={`absolute top-3 right-3 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
        p.source === "fp" ? "bg-amber-500 text-white" : "bg-slate-900 text-white"}`}>
        {p.source === "fp" ? "FP" : "RAF"}
      </span>

      <Link to={`/filho-presente/profissionais/${p.id}`} className="block group">
        <div className="flex items-start gap-4 pr-10">
          {p.photo_url ? (
            <img src={p.photo_url} alt="" className={`w-16 h-16 rounded-full object-cover border-2 border-white shadow shrink-0 ${style ? `ring-2 ring-offset-2 ${style.ring}` : ""}`} />
          ) : (
            <div className={`w-16 h-16 rounded-full flex items-center justify-center font-display font-black text-xl shrink-0 border-2 border-white shadow ${avatarBg} ${style ? `ring-2 ring-offset-2 ${style.ring}` : ""}`}
              aria-label={`Iniciais de ${p.display_name || "profissional"}`}>
              {initials(p.display_name || p.name || "")}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="font-display font-bold text-[#0B2B62] text-lg leading-tight truncate group-hover:text-emerald-700">{p.display_name}</div>
              <span title="Perfil auditado e aprovado pela curadoria RAF Direct."
                data-testid={`fp-pro-verified-${p.id}`}
                className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-600 text-white ring-1 ring-emerald-700 shadow-sm shrink-0">
                <BadgeCheck className="w-3 h-3" strokeWidth={2.5} /> Verificado
              </span>
            </div>
            {council && (
              <div className="text-xs text-emerald-700 font-semibold mt-0.5 uppercase tracking-widest flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> {council}
              </div>
            )}
            <div className="flex flex-wrap gap-1 mt-2 min-h-[22px]">
              {cats.slice(0, 3).map(c => (
                <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{c}</span>
              ))}
              {p.accepts_insurance && (
                <span
                  data-testid={`fp-pro-insurance-${p.id}`}
                  title="Este profissional aceita convênios de saúde para teleconsulta."
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                  <HeartHandshake className="w-3 h-3" /> Aceita convênios
                </span>
              )}
            </div>
          </div>
        </div>
        <p className="text-sm text-slate-600 mt-3 line-clamp-3 text-left min-h-[60px]">
          {p.bio || <span className="italic text-slate-400">Perfil disponível para agendamento — fale com o consultor RAF para saber mais sobre este profissional.</span>}
        </p>
      </Link>

      <div className="mt-auto pt-3 flex items-center justify-between gap-2 border-t border-slate-100">
        {style ? (
          <span data-testid={`fp-pro-tier-${p.id}`}
            className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-widest ${style.bg}`}>
            <Award className="w-3 h-3" /> Selo {style.label}
          </span>
        ) : (
          <span className="text-[10px] text-slate-400 uppercase tracking-widest">
            {p.city ? `${p.city}${p.state ? `/${p.state}` : ""}` : "—"}
          </span>
        )}
        <a href={waUrl} target="_blank" rel="noopener noreferrer"
          onClick={() => trackWhatsApp("fp_prof_card", { prof_id: p.id })}
          data-testid={`fp-pro-consult-${p.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold">
          <MessageCircle className="w-3.5 h-3.5" /> Falar com consultor
        </a>
      </div>
    </div>
  );
};
